import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { CartItem, Product, Store } from '../types'
import toast from 'react-hot-toast'

interface StoreCart {
  store: Store
  items: CartItem[]
}

interface CartContextType {
  storeCarts: Record<string, StoreCart> // { [storeId]: { store, items } }
  total: number
  itemCount: number
  loading: boolean
  addItem: (product: Product, store: Store, quantity?: number, notes?: string, variantId?: string, variant?: any) => void
  updateQuantity: (storeId: string, itemId: string, quantity: number) => void
  removeItem: (storeId: string, itemId: string) => void
  clearCart: () => void
  clearStoreCart: (storeId: string) => void
  getStoreCart: (storeId: string) => StoreCart | null
  isEmpty: boolean
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}

interface CartProviderProps {
  children: ReactNode
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [storeCarts, setStoreCarts] = useState<Record<string, StoreCart>>({})
  const [loading] = useState(false)

  // Cargar carrito desde localStorage al montar
  useEffect(() => {
    const loadCartFromStorage = () => {
      const savedCart = localStorage.getItem('cart')

      if (savedCart) {
        try {
          const parsedData = JSON.parse(savedCart)
          
          // Compatibilidad con formato antiguo (array de items)
          if (Array.isArray(parsedData)) {
            // Migrar formato antiguo a nuevo formato
            const oldStore = localStorage.getItem('currentStore')
            if (oldStore) {
              try {
                const parsedStore = JSON.parse(oldStore)
                if (parsedStore && parsedStore.id) {
                  const normalizedStore = {
                    ...parsedStore,
                    deliveryFee: Number(parsedStore.deliveryFee) || Number(parsedStore.delivery_fee) || 0,
                    minOrderAmount: Number(parsedStore.minOrderAmount) || Number(parsedStore.min_order_amount) || 0,
                    deliveryTime: Number(parsedStore.deliveryTime) || Number(parsedStore.delivery_time) || 30,
                    isActive: parsedStore.isActive !== undefined ? parsedStore.isActive : (parsedStore.is_active !== undefined ? parsedStore.is_active : true)
                  }
                  
                  const normalizedItems = parsedData.map((item: any) => {
                    const price = Number(item.product?.price) || 0
                    const quantity = Number(item.quantity) || 1
                    return {
                      ...item,
                      product: {
                        ...item.product,
                        price: price,
                        imageUrl: item.product?.imageUrl || item.product?.image_url || null,
                        stockQuantity: Number(item.product?.stockQuantity) || Number(item.product?.stock_quantity) || 999,
                        unit: item.product?.unit || 'unidad'
                      },
                      quantity: quantity,
                      total: quantity * price
                    }
                  }).filter((item: any) => item.product && item.product.id)
                  
                  if (normalizedItems.length > 0) {
                    setStoreCarts({
                      [normalizedStore.id]: {
                        store: normalizedStore,
                        items: normalizedItems
                      }
                    })
                    return
                  }
                }
              } catch (error) {
                console.error('Error parsing old store:', error)
              }
            }
            // Si no hay store válido, limpiar
            localStorage.removeItem('cart')
            localStorage.removeItem('currentStore')
            setStoreCarts({})
            return
          }
          
          // Nuevo formato: objeto con storeCarts
          if (typeof parsedData === 'object' && parsedData !== null) {
            const normalizedStoreCarts: Record<string, StoreCart> = {}
            
            for (const [storeId, storeCartData] of Object.entries(parsedData)) {
              const cartData = storeCartData as any
              if (cartData.store && cartData.items && Array.isArray(cartData.items)) {
                const normalizedStore = {
                  ...cartData.store,
                  deliveryFee: Number(cartData.store.deliveryFee) || Number(cartData.store.delivery_fee) || 0,
                  minOrderAmount: Number(cartData.store.minOrderAmount) || Number(cartData.store.min_order_amount) || 0,
                  deliveryTime: Number(cartData.store.deliveryTime) || Number(cartData.store.delivery_time) || 30,
                  isActive: cartData.store.isActive !== undefined ? cartData.store.isActive : (cartData.store.is_active !== undefined ? cartData.store.is_active : true)
                }
                
                const normalizedItems = cartData.items.map((item: any) => {
                  const price = Number(item.product?.price) || 0
                  const quantity = Number(item.quantity) || 1
                  return {
                    ...item,
                    product: {
                      ...item.product,
                      price: price,
                      imageUrl: item.product?.imageUrl || item.product?.image_url || null,
                      stockQuantity: Number(item.product?.stockQuantity) || Number(item.product?.stock_quantity) || 999,
                      unit: item.product?.unit || 'unidad'
                    },
                    quantity: quantity,
                    total: quantity * price
                  }
                }).filter((item: any) => item.product && item.product.id)
                
                if (normalizedItems.length > 0) {
                  normalizedStoreCarts[storeId] = {
                    store: normalizedStore,
                    items: normalizedItems
                  }
                }
              }
            }
            
            if (Object.keys(normalizedStoreCarts).length > 0) {
              setStoreCarts(normalizedStoreCarts)
            } else {
              localStorage.removeItem('cart')
              setStoreCarts({})
            }
          }
        } catch (error) {
          console.error('Error loading cart from localStorage:', error)
          localStorage.removeItem('cart')
          localStorage.removeItem('currentStore')
          setStoreCarts({})
        }
      }
    }

    loadCartFromStorage()
  }, [])

  // Guardar carrito en localStorage cuando cambia
  useEffect(() => {
    if (Object.keys(storeCarts).length > 0) {
      try {
        localStorage.setItem('cart', JSON.stringify(storeCarts))
      } catch (error) {
        console.error('Error saving cart to localStorage:', error)
      }
    } else {
      localStorage.removeItem('cart')
      localStorage.removeItem('currentStore') // Limpiar también el formato antiguo
    }
  }, [storeCarts])

  const addItem = (product: Product, store: Store, quantity = 1, notes?: string, variantId?: string, variant?: any) => {
    // Normalizar el precio del producto
    const normalizedProduct = {
      ...product,
      price: Number(product.price) || 0
    }

    // Normalizar el store
    const normalizedStore = {
      ...store,
      deliveryFee: Number(store.deliveryFee) || Number((store as any).delivery_fee) || 0,
      minOrderAmount: Number(store.minOrderAmount) || Number((store as any).min_order_amount) || 0,
      deliveryTime: Number(store.deliveryTime) || Number((store as any).delivery_time) || 30,
      isActive: store.isActive !== undefined ? store.isActive : ((store as any).is_active !== undefined ? (store as any).is_active : true)
    }

    setStoreCarts(currentCarts => {
      const storeId = normalizedStore.id
      const existingCart = currentCarts[storeId]

      // Crear ID único para el item (incluye variantId si existe)
      const itemId = variantId 
        ? `${normalizedProduct.id}-${variantId}-${Date.now()}`
        : `${normalizedProduct.id}-${Date.now()}`

      if (existingCart) {
        // Store ya existe en el carrito
        // Buscar item existente con mismo producto y variante
        const existingItem = existingCart.items.find(item => 
          item.product.id === normalizedProduct.id && 
          item.variantId === variantId
        )

        if (existingItem) {
          // Actualizar elemento existente
          const updatedItems = existingCart.items.map(item =>
            item.id === existingItem.id
              ? {
                  ...item,
                  quantity: item.quantity + quantity,
                  notes: notes || item.notes,
                  total: (item.quantity + quantity) * normalizedProduct.price
                }
              : item
          )
          return {
            ...currentCarts,
            [storeId]: {
              store: normalizedStore,
              items: updatedItems
            }
          }
        } else {
          // Agregar nuevo elemento al store existente
          const newItem: CartItem = {
            id: itemId,
            product: normalizedProduct,
            quantity,
            notes,
            total: quantity * normalizedProduct.price,
            variantId,
            variant: variant ? {
              id: variant.id,
              variant_name: variant.variant_name || variant.variantName,
              variant_type: variant.variant_type || variant.variantType,
              stock_quantity: variant.stock_quantity || variant.stockQuantity,
              is_available: variant.is_available !== undefined ? variant.is_available : true,
              sku: variant.sku,
              image_url: variant.image_url || variant.imageUrl
            } : undefined
          }
          return {
            ...currentCarts,
            [storeId]: {
              store: normalizedStore,
              items: [...existingCart.items, newItem]
            }
          }
        }
      } else {
        // Nuevo store en el carrito
        const newItem: CartItem = {
          id: itemId,
          product: normalizedProduct,
          quantity,
          notes,
          total: quantity * normalizedProduct.price,
          variantId,
          variant: variant ? {
            id: variant.id,
            variant_name: variant.variant_name || variant.variantName,
            variant_type: variant.variant_type || variant.variantType,
            stock_quantity: variant.stock_quantity || variant.stockQuantity,
            is_available: variant.is_available !== undefined ? variant.is_available : true,
            sku: variant.sku,
            image_url: variant.image_url || variant.imageUrl
          } : undefined
        }
        return {
          ...currentCarts,
          [storeId]: {
            store: normalizedStore,
            items: [newItem]
          }
        }
      }
    })

    const variantName = variant ? (variant.variant_name || variant.variantName) : ''
    const productName = variantName ? `${product.name} - ${variantName}` : product.name
    toast.success(`${quantity} ${productName} agregado al carrito de ${normalizedStore.name}`)
  }

  const updateQuantity = (storeId: string, itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(storeId, itemId)
      return
    }

    setStoreCarts(currentCarts => {
      const storeCart = currentCarts[storeId]
      if (!storeCart) return currentCarts

      const updatedItems = storeCart.items.map(item =>
        item.id === itemId
          ? {
              ...item,
              quantity,
              total: quantity * (Number(item.product.price) || 0)
            }
          : item
      )

      return {
        ...currentCarts,
        [storeId]: {
          ...storeCart,
          items: updatedItems
        }
      }
    })
  }

  const removeItem = (storeId: string, itemId: string) => {
    setStoreCarts(currentCarts => {
      const storeCart = currentCarts[storeId]
      if (!storeCart) return currentCarts

      const item = storeCart.items.find(i => i.id === itemId)
      const filteredItems = storeCart.items.filter(i => i.id !== itemId)

      // Si el carrito del store queda vacío, eliminar el store del carrito
      if (filteredItems.length === 0) {
        const { [storeId]: removed, ...rest } = currentCarts
        return rest
      }

      if (item) {
        toast.success(`${item.product.name} removido del carrito`)
      }

      return {
        ...currentCarts,
        [storeId]: {
          ...storeCart,
          items: filteredItems
        }
      }
    })
  }

  const clearCart = () => {
    setStoreCarts({})
    localStorage.removeItem('cart')
    localStorage.removeItem('currentStore')
    toast.success('Carrito vaciado')
  }

  const clearStoreCart = (storeId: string) => {
    setStoreCarts(currentCarts => {
      const { [storeId]: removed, ...rest } = currentCarts
      return rest
    })
    toast.success('Carrito del negocio vaciado')
  }

  const getStoreCart = (storeId: string): StoreCart | null => {
    return storeCarts[storeId] || null
  }

  // Calcular totales globales
  const total = Object.values(storeCarts).reduce((sum, storeCart) => {
    return sum + storeCart.items.reduce((itemSum, item) => itemSum + (Number(item.total) || 0), 0)
  }, 0)

  const itemCount = Object.values(storeCarts).reduce((sum, storeCart) => {
    return sum + storeCart.items.reduce((itemSum, item) => itemSum + (item.quantity || 0), 0)
  }, 0)

  const isEmpty = Object.keys(storeCarts).length === 0

  const value: CartContextType = {
    storeCarts,
    total,
    itemCount,
    loading,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    clearStoreCart,
    getStoreCart,
    isEmpty
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}
